import WarpElement from '@warp-ds/elements-core';
import { css } from 'lit';
import { html } from 'lit/static-html.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { fetchSvgContent } from '../../../lib/svgFetcher.js';
import '@warp-ds/components/icon';

const safe = (val) => val || '';

export class MarketIcon extends WarpElement {
    static styles = [
        ...WarpElement.styles,
        css`
            .two-lines {
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 2;
                overflow: hidden;
                text-overflow: ellipsis;
            }
            *,:before,:after{--w-rotate:0;--w-rotate-x:0;--w-rotate-y:0;--w-rotate-z:0;--w-scale-x:1;--w-scale-y:1;--w-scale-z:1;--w-skew-x:0;--w-skew-y:0;--w-translate-x:0;--w-translate-y:0;--w-translate-z:0}.static{position:static}.s-icon-secondary{color:var(--w-s-color-icon-secondary)}.s-icon-subtle-hover{color:var(--w-s-color-icon-subtle-hover)};
        `,
    ];

    static properties = {
        market: { type: Object, attribute: 'market', reflect: true },
        locale: { type: String, attribute: 'locale', reflect: true },
        imageLoadError: { type: Boolean, state: true },
        svgContent: { type: String, state: true },
        brand: { type: String, attribute: 'brand', reflect: true },
        isHovered: { type: Boolean, attribute: 'is-hovered', reflect: true },
    };

    connectedCallback() {
        super.connectedCallback();
        this.imageLoadError = false;
        this.svgContent = '';
    }

    disconnectedCallback() {
        super.disconnectedCallback();

    }


    handleImageError() {
        this.imageLoadError = true;
    }


    get renderEmbedded() {
        if (this.market.icon?.icon_type !== "embedded") {
            throw new Error("Not an embedded icon type");
        }
        
        const { icon } = this.market;
        const embedded = icon.variants?.find((variant) => variant.id);
        
        if (!embedded || !embedded.id) {
            throw new Error("No valid embedded variant found");
        }
        
        const iconName = embedded.id;
        return html`<w-icon name="${iconName}" size="large" locale="${this.locale}" class="${this.iconClass}"></w-icon>`;
    }

    get renderLogo() {
        if (this.market.icon?.icon_type !== "logo") {
            throw new Error("Not a logo icon type");
        }
        
        if (this.imageLoadError) {
            throw new Error("Image failed to load");
        }
        
        const { icon } = this.market;
        const logo = icon.variants?.find((variant) => variant.img_format === "svg");
        
        if (!logo || !logo.locations) {
            throw new Error("No valid logo variant found");
        }
        
        const logoUrl = logo.locations.light;
        
        if (!logoUrl) {
            throw new Error("No valid logo URL found");
        }
        
        return html`<img 
            src="${logoUrl}" 
            alt="${safe(icon.description)}"
            @error=${this.handleImageError}
            loading="lazy"
          />`;
    }

    get renderSvgIcon() {
        if (this.market.icon?.icon_type !== "icon") {
            throw new Error("Not an icon type");
        }
        
        if (this.imageLoadError) {
            throw new Error("SVG failed to load");
        }
        
        const { icon } = this.market;
        const svg = icon.variants?.find((variant) => variant.img_format === "svg");
        
        if (!svg || !svg.locations) {
            throw new Error("No valid icon variant found");
        }
        
        const iconUrl = svg.locations.icon;
        
        if (!iconUrl) {
            throw new Error("No valid icon URL found");
        }
        
        // Fetch SVG content when URL changes or when we don't have content
        if (iconUrl && (iconUrl !== this._currentSvgUrl || !this.svgContent)) {
            this._currentSvgUrl = iconUrl;
            this.svgContent = '';
            this.imageLoadError = false;
            
            fetchSvgContent(iconUrl).then(result => {
                this.svgContent = result.svgContent;
                this.imageLoadError = result.error;
            });
        }
        
        if (this.svgContent) {
            return html`<div class="${this.iconClass}" data-testid="svg-content">${unsafeHTML(this.svgContent)}</div>`;
        } else {
            return html`<w-icon name="Image" size="large" locale="${this.locale}" class="${this.iconClass}"></w-icon>`;
        }
    }

    get renderMarketIcon() {
        if (!this.market?.icon) {
            return html`<w-icon name="Image" size="large" locale="${this.locale}" class="${this.iconClass}"></w-icon>`;
        }

        const iconType = this.market.icon.icon_type;

        try {
            switch (iconType) {
                case 'embedded':
                    return this.renderEmbedded;
                case 'logo':
                    return this.renderLogo;
                case 'icon':
                    return this.renderSvgIcon;
                default:
                    return html`<w-icon name="Image" size="large" locale="${this.locale}" class="${this.iconClass}"></w-icon>`;
            }
        } catch (error) {
            console.warn(`Failed to render ${iconType} variant:`, error.message);
            return html`<w-icon name="Image" size="large" locale="${this.locale}" class="${this.iconClass}"></w-icon>`;
        }
    }


    get iconClass() {
        return `${this.isHovered ? "s-icon-subtle-hover" : "s-icon-secondary"}`;
    }

    render() {
        return this.renderMarketIcon;
    }
}

if (!customElements.get('market-icon')) {
    customElements.define('market-icon', MarketIcon);
}
